import {MQ_ENDPOINTS} from '~/constants';
import {ThunkApi} from '~/utils/thunk-api';
import {DeleteParams, FetchParams, PostParams, PutParams} from '~/utils/thunk-api/type';
import {
  QuestionnaireEntity,
  NormalizedQuestionnaire,
  MedicalQuestionnaireKey,
  AnswerData,
  WPAuthUser,
} from '~/redux/questionnaires/types';
import {questionnaireSchema} from '~/redux/questionnaires/entity-config';
import {BlockScreenType} from '~/constants/medical-questionnaires';

export interface FetchQuestionnairesParams extends FetchParams {
  methodKey: string;
  // wpUserId: number;
}

export interface FetchQuestionnairesAnswerParams extends FetchParams {
  mdqId: string;
}

export interface FetchAppointmentTimeParams extends FetchParams {
  date: string;
  userQuestionnaireId: number;
}

export interface FetchAppointmentDateParams extends FetchParams {
  startTime: string;
  endTime: string;
  userQuestionnaireId: number;
}

export interface AddQuestionnaireParams extends PostParams {
  data: Partial<QuestionnaireEntity>;
}

export interface BookAppointmentParams extends PostParams {
  data: {
    startTime?: string | null;
    userQuestionnaireId?: number;
    orderId?: number;
  };
  platform?: number;
}

export interface TrackMedicalQuestionnaireScreenParams extends PostParams {
  data: {
    methodKey: string;
    mdqKey: string;
    screen: string;
    wpAuthUser: WPAuthUser;
    collectData: any;
    blockScreenType?: string;
  };
}

export interface UpdateQuestionnaireParams extends PutParams {
  data: Partial<QuestionnaireEntity>;
  id: number;
}

const thunk = new ThunkApi<QuestionnaireEntity, NormalizedQuestionnaire>('questionnaire', questionnaireSchema, false);
export const fetchQuestionnaires = thunk.fetchOne<FetchQuestionnairesParams>(MQ_ENDPOINTS.QUESTIONNAIRES, '', {
  withoutNormalize: true,
});

export const fetchAppointmentTime = thunk.fetch<FetchAppointmentTimeParams>(MQ_ENDPOINTS.APPOINTMENT_TIME, 'time', {
  withoutNormalize: true,
});

export const fetchAppointmentDate = thunk.fetch<FetchAppointmentDateParams>(MQ_ENDPOINTS.APPOINTMENT_DATE, 'date', {
  withoutNormalize: true,
});

export const bookAppointment = thunk.post<BookAppointmentParams>(MQ_ENDPOINTS.BOOKING_APPOINTMENT, 'bookAppointment', {
  withoutNormalize: true,
});

export const addQuestionnaire = thunk.post<AddQuestionnaireParams>(MQ_ENDPOINTS.QUESTIONNAIRES, 'add', {
  withoutNormalize: true,
});
export const updateQuestionnaire = thunk.put<UpdateQuestionnaireParams>(`${MQ_ENDPOINTS.QUESTIONNAIRES}/:id`);
export const deleteQuestionnaire = thunk.delete<DeleteParams>(`${MQ_ENDPOINTS.QUESTIONNAIRES}/:id`);

export const fetchQuestionnairesAnswerById = thunk.fetch<FetchQuestionnairesAnswerParams, QuestionnaireEntity>(
  `${MQ_ENDPOINTS.QUESTIONNAIRES}/:mdqId`,
  'fetchMDQAnswer',
  {
    withoutNormalize: true,
  },
);

export const trackMedicalQuestionnaireScreen = thunk.post<TrackMedicalQuestionnaireScreenParams>(
  `${MQ_ENDPOINTS.QUESTIONNAIRES}/tracking`,
  'trackMedicalQuestionnaireScreen',
);

export const mergeDataObjectsWithKeys = ({
  rootCollectData,
  currentAnswer,
}: {
  rootCollectData: AnswerData['data'];
  currentAnswer: AnswerData['data'];
}) => {
  let newRootCollectData = rootCollectData ? {...rootCollectData} : {};
  if (newRootCollectData?.user && currentAnswer?.user) {
    const profile = Object.keys(newRootCollectData.user).reduce((obj: Record<string, string>, key) => {
      if (key in currentAnswer.user) {
        obj[key as keyof typeof obj] = currentAnswer.user[key];
      }
      return obj;
    }, {});
    return {
      user: profile,
    };
  } else {
    return Object.keys(newRootCollectData).reduce((obj: Record<string, string>, key) => {
      if (key in currentAnswer) {
        obj[key as keyof typeof obj] = currentAnswer[key];
      }
      return obj;
    }, {});
  }
};

export const trackingSorryPageMisc = thunk.misc<{
  methodKey: string;
  mdqKey: string;
  screen: string;
  wpAuthUser: WPAuthUser;
  collectData: {};
  blockScreenType?: BlockScreenType;
}>(
  'trackingSorryPageMisc',
  async ({methodKey, mdqKey, screen, wpAuthUser, collectData = {}, blockScreenType}, dispatch) => {
    await dispatch(
      trackMedicalQuestionnaireScreen({
        data: {
          methodKey,
          mdqKey,
          screen,
          wpAuthUser,
          collectData,
          blockScreenType,
        },
      }),
    );
  },
);
